import { useId } from 'react';
import type { FocusEventHandler } from 'react';
import styles from './emoji-field.module.css';

const NO_EMOJI = '';

type EmojiFieldProps = {
  label: string;
  name: string;
  value: string;
  presets: readonly string[];
  onChange: (value: string) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  hint?: string;
  error?: string;
};

/**
 * A grid to tap plus one free cell to type or paste into: a phone has an emoji keyboard, a
 * desktop usually has none, so a bare text input leaves the desktop user with nothing to pick from.
 */
export const EmojiField = ({
  label,
  name,
  value,
  presets,
  onChange,
  onBlur,
  hint,
  error,
}: EmojiFieldProps) => {
  const fieldId = useId();
  const messageId = `${fieldId}-message`;
  const hasError = error !== undefined;
  const message = error ?? hint;
  const isCustom = value !== NO_EMOJI && !presets.includes(value);

  return (
    <fieldset
      className={styles.group}
      aria-describedby={message === undefined ? undefined : messageId}
      aria-invalid={hasError}
    >
      <legend className={styles.legend}>{label}</legend>
      <div className={styles.chips}>
        <button
          type="button"
          className={`${styles.chip} ${styles.none} ${value === NO_EMOJI ? styles.selected : ''}`}
          aria-pressed={value === NO_EMOJI}
          aria-label="Без емодзі"
          onClick={() => {
            onChange(NO_EMOJI);
          }}
        >
          —
        </button>
        {presets.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className={`${styles.chip} ${value === emoji ? styles.selected : ''}`}
            aria-pressed={value === emoji}
            aria-label={emoji}
            onClick={() => {
              onChange(emoji);
            }}
          >
            {emoji}
          </button>
        ))}
        <input
          className={`${styles.chip} ${styles.customInput} ${isCustom ? styles.selected : ''}`}
          name={name}
          value={isCustom ? value : ''}
          placeholder="…"
          aria-label="Свій емодзі"
          autoComplete="off"
          onChange={(event) => {
            onChange(event.target.value);
          }}
          onBlur={onBlur}
        />
      </div>
      {message !== undefined && (
        <p
          id={messageId}
          className={hasError ? styles.error : styles.hint}
          role={hasError ? 'alert' : undefined}
        >
          {message}
        </p>
      )}
    </fieldset>
  );
};
